<script lang="ts">
  import { api } from '../../lib/api';
  import { DECKLINK_OUTPUT_MODES, normalizeOutput } from '../../../../shared/src/defaults';
  import type { OutputConfig } from '../../../../shared/src/types';
  import { clone, type Overview } from './types';

  let { data, reload }: { data: Overview; reload: () => void } = $props();
  let draft = $state<OutputConfig | null>(null);
  let error = $state<string | null>(null);
  let pipeline = $state<string | null>(null);

  const sourceName = (id: string | null) =>
    !id ? '— none —' : data.sources.find((s) => s.id === id)?.name ?? data.browserSources.find((b) => b.id === id)?.name ?? id;

  function edit(o: OutputConfig | null) {
    error = null;
    draft = o ? clone(o) : normalizeOutput({ name: 'Commentator', kind: 'ndi', sourceId: data.browserSources[0]?.id ?? null });
  }
  function setKind(kind: OutputConfig['kind']) {
    if (draft) draft = normalizeOutput({ ...draft, kind });
  }
  async function save() {
    if (!draft) return;
    try {
      await api.post('/api/admin/outputs', draft);
      draft = null;
      reload();
    } catch (err) {
      error = (err as Error).message;
    }
  }
  async function remove(o: OutputConfig) {
    if (!confirm(`Delete output "${o.name}"?`)) return;
    await api.del(`/api/admin/outputs/${o.id}`);
    reload();
  }
  async function toggle(o: OutputConfig) {
    await api.post('/api/admin/outputs', { ...o, enabled: !o.enabled });
    reload();
  }
  async function show(o: OutputConfig) {
    const r = await api.get<{ pipeline: string | null }>(`/api/admin/outputs/${o.id}/pipeline`);
    pipeline = (r.pipeline ?? 'not running').replace(/ {2}/g, '\n\n').replace(/ ! /g, ' !\n  ');
  }
</script>

<div class="panel">
  <div class="row"><h3 class="grow">Outputs (NDI / SDI)</h3><button class="primary" onclick={() => edit(null)}>Add output</button></div>
  <p class="muted">Play a source — typically a browser camera such as a remote commentator — out of the server so the vision mixer can take it. Each output decodes and rescales one feed (≈0.5–1 CPU core at 720p, more at 1080). When the feed stops the output shows a NO SIGNAL slate with silent audio.</p>
  {#each data.outputs as o (o.id)}
    {@const st = data.outputStatus.find((s) => s.id === o.id)}
    <div class="list-item">
      <b class="grow">{o.name}</b>
      <span class="badge">{o.kind === 'ndi' ? `NDI "${o.ndiName}"` : `DeckLink #${o.decklinkDevice} ${o.decklinkMode}`}</span>
      <span class="muted">from {sourceName(o.sourceId)}</span>
      {#if !o.enabled}<span class="badge">disabled</span>
      {:else if st}
        <span class="badge {st.state === 'running' ? 'ok' : st.state === 'starting' || st.state === 'backoff' ? 'warn' : 'bad'}">{st.state}</span>
        <span class="badge {st.input === 'live' ? 'ok' : 'warn'}">{st.input === 'live' ? `live ${st.fps} fps` : 'slate'}</span>
        {#if st.lastError}<span class="error small">{st.lastError}</span>{/if}
      {/if}
      <button class="small" onclick={() => edit(o)}>Edit</button>
      <button class="small" onclick={() => toggle(o)}>{o.enabled ? 'Disable' : 'Enable'}</button>
      <button class="small" onclick={() => show(o)}>Pipeline</button>
      <button class="small danger" onclick={() => remove(o)}>Delete</button>
    </div>
  {:else}<p class="muted">No outputs.</p>{/each}
  {#if !data.caps.ndi}<p class="error small">NDI plugin not installed: NDI outputs will fail (scripts/install-ndi.sh).</p>{/if}
</div>

{#if pipeline}
  <div class="panel" style="margin-top:12px">
    <div class="row"><h3 class="grow">GStreamer pipeline</h3><button class="small" onclick={() => (pipeline = null)}>Close</button></div>
    <pre class="mono">{pipeline}</pre>
  </div>
{/if}

{#if draft}
  <div class="panel" style="margin-top:12px">
    <div class="form">
      <label>Name <input bind:value={draft.name} /></label>
      <label>Source
        <select bind:value={draft.sourceId}>
          <option value={null}>— none —</option>
          {#each data.browserSources as b (b.id)}<option value={b.id}>📷 {b.name}{b.live ? '' : ' (offline)'}</option>{/each}
          {#each data.sources as s (s.id)}<option value={s.id}>{s.name}</option>{/each}
        </select>
      </label>
      <label>Type
        <select value={draft.kind} onchange={(e) => setKind(e.currentTarget.value as OutputConfig['kind'])}>
          <option value="ndi">NDI source</option><option value="decklink">DeckLink SDI output</option>
        </select>
      </label>
      <label class="inline"><input type="checkbox" bind:checked={draft.enabled} /> Enabled</label>
      {#if draft.kind === 'ndi'}
        <label>NDI name <input bind:value={draft.ndiName} /></label>
        <label>Width <input type="number" bind:value={draft.width} /></label>
        <label>Height <input type="number" bind:value={draft.height} /></label>
        <label>Frame rate <input type="number" bind:value={draft.framerate} /></label>
      {:else}
        <label>DeckLink device <input type="number" min="0" bind:value={draft.decklinkDevice} /></label>
        <label>Output mode
          <select bind:value={draft.decklinkMode}>{#each Object.keys(DECKLINK_OUTPUT_MODES) as m (m)}<option>{m}</option>{/each}</select>
        </label>
        <p class="wide muted">Use an output connector that is not also an input of an SDI source. Interlaced modes are woven from progressive frames.</p>
      {/if}
    </div>
    {#if error}<p class="error">{error}</p>{/if}
    <div class="row" style="margin-top:12px"><button class="primary" onclick={save}>Save &amp; apply</button><button onclick={() => (draft = null)}>Cancel</button></div>
  </div>
{/if}

<style>
  pre { white-space: pre-wrap; font-size: 0.78rem; max-height: 420px; overflow: auto; background: #0a0a0a; padding: 12px; border-radius: 8px; }
  .small { font-size: 0.8rem; }
</style>
